// ════════════════════════════════════════════════════════════════════════════
//  Contas a Receber — visão GERENCIAL (Fase 5).
//
//  A tela operacional responde "o que eu recebo hoje" (uma linha por parcela).
//  Esta responde "quem me deve e como está cada acordo", com drill-down:
//
//      CLIENTE (sintético)  →  TÍTULOS do cliente  →  parcelas (drawer)
//
//  Espelha BuscarContasReceberGrupoSintetico / BuscarContasReceberGrupo do
//  desktop, e traz o filtro de BASE DA DATA (emissão / vencimento / pagamento),
//  que muda a pergunta que a tela responde.
//
//  Consulta pura: nada aqui baixa nem estorna. As operações continuam na tela
//  operacional — misturar as duas foi a origem dos bugs desta trilha.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import {
  Search, X, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon,
  DollarSign, AlertCircle, CheckCircle, Users, Eye, Timer, ArrowLeft,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useGerencialClientesReceber, useGerencialTitulosReceber, usePrazoMedioRecebimento,
} from "@/features/financeiro/hooks/useContasReceber";
import { ContaReceberDrawer } from "@/features/financeiro/components/ContaReceberDrawer";
import type {
  BaseDataFinanceira, SituacaoAcordo, ContaReceberClienteResumoDto,
} from "@/features/financeiro/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PAGE_SIZES = [20, 50, 100];

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const situacaoCor: Record<string, string> = {
  Quitado:   "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  Parcial:   "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
  Aberto:    "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
  Cancelado: "bg-muted text-muted-foreground hover:bg-muted",
};

const ContasReceberGerencial = () => {
  // ── Filtros (compartilhados pelas três visões) ───────────────────────────
  const [searchTerm, setSearchTerm]   = useState("");
  const [dtInicio, setDtInicio]       = useState("");
  const [dtFim, setDtFim]             = useState("");
  const [baseData, setBaseData]       = useState<BaseDataFinanceira>("vencimento");
  const [situacao, setSituacao]       = useState<SituacaoAcordo>("todos");
  const [pagina, setPagina]           = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Cliente aberto no drill-down (null = está no sintético).
  const [clienteAberto, setClienteAberto] = useState<ContaReceberClienteResumoDto | null>(null);
  const [detalheTituloId, setDetalheTituloId] = useState<number | null>(null);

  const busca = useDebounce(searchTerm, 400);

  useEffect(() => {
    setPagina(1);
  }, [busca, dtInicio, dtFim, baseData, situacao, tamanhoPagina, clienteAberto]);

  const filtroBase = {
    busca,
    dtInicio: dtInicio || undefined,
    dtFim: dtFim || undefined,
    baseData,
    situacao,
  };

  const { clientes, total, totalPaginas, totais, isLoading } =
    useGerencialClientesReceber({ ...filtroBase, pagina, tamanhoPagina });

  const {
    titulos, total: totalTitulos, totalPaginas: totalPaginasTitulos,
    isLoading: carregandoTitulos,
  } = useGerencialTitulosReceber(
    { ...filtroBase, clienteId: clienteAberto?.clienteId, pagina, tamanhoPagina },
    !!clienteAberto,
  );

  const prazo = usePrazoMedioRecebimento({ ...filtroBase, dtInicio: dtInicio || undefined });

  const temFiltro = !!busca || !!dtInicio || !!dtFim ||
    baseData !== "vencimento" || situacao !== "todos";

  function limparFiltros() {
    setSearchTerm(""); setDtInicio(""); setDtFim("");
    setBaseData("vencimento"); setSituacao("todos");
  }

  // A base "pagamento" só enxerga o que já foi recebido — avisar é melhor do
  // que deixar o usuário concluir que "sumiram títulos".
  const avisoBasePagamento = baseData === "pagamento";

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contas a Receber — Gerencial</h1>
          <p className="text-muted-foreground mt-1">
            Quem deve e como está cada acordo — clique num cliente para abrir os títulos
          </p>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Saldo a Receber
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{brl(totais.totalEmAberto)}</div>
            <p className="text-xs text-muted-foreground mt-1">{totais.qtdEmAberto} parcela(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Vencido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{brl(totais.totalAtrasado)}</div>
            <p className="text-xs text-muted-foreground mt-1">{totais.qtdAtrasado} parcela(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Recebido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{brl(totais.totalQuitado)}</div>
            <p className="text-xs text-muted-foreground mt-1">{totais.qtdQuitado} parcela(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Timer className="h-4 w-4" /> Prazo Médio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {prazo.isLoading ? "…" : `${prazo.prazoMedioDias.toLocaleString("pt-BR")} dias`}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              da emissão ao recebimento
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filtros ── */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-end">
            <div className="relative flex-1 min-w-[14rem]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente, nº documento ou duplicata..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground leading-none mb-1">Data por</span>
              <Select value={baseData} onValueChange={(v) => setBaseData(v as BaseDataFinanceira)}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vencimento">Vencimento</SelectItem>
                  <SelectItem value="emissao">Emissão</SelectItem>
                  <SelectItem value="pagamento">Pagamento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground leading-none mb-1">De</span>
              <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="w-36" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground leading-none mb-1">até</span>
              <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="w-36" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground leading-none mb-1">Situação</span>
              <Select value={situacao} onValueChange={(v) => setSituacao(v as SituacaoAcordo)}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberto">Em aberto</SelectItem>
                  <SelectItem value="quitado">Quitados</SelectItem>
                  <SelectItem value="cancelado">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Select value={String(tamanhoPagina)} onValueChange={(v) => setTamanhoPagina(Number(v))}>
              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {temFiltro && (
              <Button variant="ghost" size="sm" onClick={limparFiltros} title="Limpar filtros" className="gap-1">
                <X className="h-4 w-4" /> Limpar
              </Button>
            )}
          </div>

          {avisoBasePagamento && (
            <p className="text-xs text-muted-foreground mt-3 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Com a data por <strong>pagamento</strong>, só aparece o que já foi recebido —
              parcela em aberto não tem data de pagamento.
            </p>
          )}
        </CardHeader>
      </Card>

      <Tabs defaultValue="carteira">
        <TabsList>
          <TabsTrigger value="carteira">Carteira</TabsTrigger>
          <TabsTrigger value="prazo">Prazo médio</TabsTrigger>
        </TabsList>

        {/* ── Carteira: sintético por cliente → títulos ── */}
        <TabsContent value="carteira" className="mt-4">
          <Card>
            <CardHeader>
              {clienteAberto ? (
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" className="gap-1" onClick={() => setClienteAberto(null)}>
                    <ArrowLeft className="h-4 w-4" /> Todos os clientes
                  </Button>
                  <div>
                    <CardTitle className="text-lg">
                      {clienteAberto.clienteNome || `Cliente #${clienteAberto.clienteId}`}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {totalTitulos} título(s) · saldo {brl(clienteAberto.saldo)}
                      {clienteAberto.saldoVencido > 0 && (
                        <span className="text-red-500"> · vencido {brl(clienteAberto.saldoVencido)}</span>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <CardTitle>Carteira por cliente</CardTitle>
                  <span className="text-sm text-muted-foreground">({total} cliente(s))</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {/* Nível 1 — sintético por cliente */}
                {!clienteAberto ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Grupo</TableHead>
                        <TableHead className="text-right">Títulos</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Recebido</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                        <TableHead className="text-right">Vencido</TableHead>
                        <TableHead className="text-right">Atraso</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                        </TableRow>
                      ) : clientes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                            Nenhum cliente no recorte
                          </TableCell>
                        </TableRow>
                      ) : clientes.map((c) => (
                        <TableRow
                          key={c.clienteId}
                          className="cursor-pointer"
                          onClick={() => setClienteAberto(c)}
                          title="Abrir os títulos deste cliente"
                        >
                          <TableCell><ChevronRightIcon className="h-4 w-4 text-muted-foreground" /></TableCell>
                          <TableCell className="font-medium">
                            {c.clienteNome || `Cliente #${c.clienteId}`}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {c.grupoClienteNome || "-"}
                          </TableCell>
                          <TableCell className="text-right">{c.qtdTitulos}</TableCell>
                          <TableCell className="text-right">{brl(c.valorTotal)}</TableCell>
                          <TableCell className="text-right text-green-600">{brl(c.valorRecebido)}</TableCell>
                          <TableCell className="text-right font-medium">{brl(c.saldo)}</TableCell>
                          <TableCell className="text-right">
                            {c.saldoVencido > 0
                              ? <span className="text-red-500 font-medium">{brl(c.saldoVencido)}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            {c.diasMaiorAtraso > 0
                              ? <Badge variant="outline" className="text-red-500 border-red-500/40">
                                  {c.diasMaiorAtraso}d
                                </Badge>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  /* Nível 2 — títulos do cliente */
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Documento</TableHead>
                        <TableHead>Emissão</TableHead>
                        <TableHead>Vencimentos</TableHead>
                        <TableHead className="text-right">Parcelas</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Recebido</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {carregandoTitulos ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                        </TableRow>
                      ) : titulos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                            Nenhum título deste cliente no recorte
                          </TableCell>
                        </TableRow>
                      ) : titulos.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">
                            {t.numDocumento || t.duplicata || `#${t.id}`}
                          </TableCell>
                          <TableCell>{new Date(t.dtEmissao).toLocaleDateString("pt-BR")}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {t.dtPrimeiroVencimento
                              ? new Date(t.dtPrimeiroVencimento).toLocaleDateString("pt-BR")
                              : "—"}
                            {t.dtUltimoVencimento && t.dtUltimoVencimento !== t.dtPrimeiroVencimento && (
                              <> → {new Date(t.dtUltimoVencimento).toLocaleDateString("pt-BR")}</>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {t.qtdParcelasPagas}/{t.qtdParcelas}
                          </TableCell>
                          <TableCell className="text-right">{brl(t.valorTotal)}</TableCell>
                          <TableCell className="text-right text-green-600">{brl(t.valorRecebido)}</TableCell>
                          <TableCell className="text-right font-medium">
                            {brl(t.saldo)}
                            {t.saldoVencido > 0 && (
                              <div className="text-xs text-red-500">
                                {brl(t.saldoVencido)} vencido · {t.diasMaiorAtraso}d
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={situacaoCor[t.situacao] ?? ""}>{t.situacao}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setDetalheTituloId(t.id)}
                              title="Ver as parcelas deste título"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Paginação (do nível corrente) */}
              {(clienteAberto ? totalPaginasTitulos : totalPaginas) > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Página {pagina} de {clienteAberto ? totalPaginasTitulos : totalPaginas}
                    {" "}({clienteAberto ? totalTitulos : total} registros)
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPagina((p) => p - 1)} disabled={pagina <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setPagina((p) => p + 1)}
                      disabled={pagina >= (clienteAberto ? totalPaginasTitulos : totalPaginas)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Prazo médio de recebimento ── */}
        <TabsContent value="prazo" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-muted-foreground" />
                <CardTitle>Prazo médio de recebimento</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">
                Dias entre a emissão e o recebimento das parcelas já recebidas.
                O período filtra pela data de pagamento. Pior prazo primeiro.
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Prazo médio</TableHead>
                      <TableHead className="text-right">Parcelas</TableHead>
                      <TableHead className="text-right">Recebido</TableHead>
                      <TableHead className="text-right">Ticket médio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prazo.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                      </TableRow>
                    ) : prazo.porCliente.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          Nenhum recebimento no recorte
                        </TableCell>
                      </TableRow>
                    ) : prazo.porCliente.map((c) => (
                      <TableRow key={c.clienteId}>
                        <TableCell className="font-medium">
                          {c.clienteNome || `Cliente #${c.clienteId}`}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {c.prazoMedioDias.toLocaleString("pt-BR")} dias
                        </TableCell>
                        <TableCell className="text-right">{c.qtdParcelas}</TableCell>
                        <TableCell className="text-right">{brl(c.valorRecebido)}</TableCell>
                        <TableCell className="text-right">{brl(c.valorMedio)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Nível 3 — as parcelas do título, no drawer que já existia. */}
      <ContaReceberDrawer id={detalheTituloId} onClose={() => setDetalheTituloId(null)} />
    </div>
  );
};

export default ContasReceberGerencial;
